import { computed, Injectable, signal } from '@angular/core';
import { IWizardContext, IWizardContextSnapshot, IWizardStepState } from '../interfaces/IWizard';

@Injectable({
  providedIn: 'root',
})
export class WizardContext implements IWizardContext {

  private readonly _activeStepId = signal(0);
  private readonly _stepStates   = signal<Record<number, IWizardStepState>>({});

  readonly activeStepId = this._activeStepId.asReadonly();

  readonly allData = computed(() => {
    const states = this._stepStates();
    const result: Record<number, unknown> = {};

    for (const [stepId, state] of Object.entries(states)) {
      if (state.data !== undefined) {
        result[Number(stepId)] = state.data;
      }
    }

    return result;
  });
  
  getStepState<TData>(stepId: number): IWizardStepState<TData> | undefined {
    return this._stepStates()[stepId] as IWizardStepState<TData> | undefined;
  }

   patchStepState<TData>(stepId: number, patch: Partial<IWizardStepState<TData>>): void {
    this._stepStates.update(current => {
      const existingState = current[stepId] ?? this.emptyStepState();

      return {
        ...current,
        [stepId]: { ...existingState, ...patch },
      };
    });
  }

  next(): void {
    this._activeStepId.update(current => current + 1);
  }

  previous(): void {
    this._activeStepId.update(current => Math.max(0, current - 1));
  }

  goTo(stepId: number): void {
    this._activeStepId.set(stepId);
  }

  snapshot(): IWizardContextSnapshot {
    return {
      activeStepId: this._activeStepId(),
      stepStates: this.cloneStepStates(this._stepStates()),
    };
  }

  restore(snapshot: IWizardContextSnapshot): void {
    this._activeStepId.set(snapshot.activeStepId);
    this._stepStates.set(this.cloneStepStates(snapshot.stepStates));
  }

  reset(): void {
    this._activeStepId.set(0);
    this._stepStates.set({});
  }

  private emptyStepState(): IWizardStepState {
    return {
      visited:   false,
      dirty:     false,
      valid:     false,
      completed: false,
    };
  }

  private cloneStepStates(
    stepStates: Record<number, IWizardStepState>,
  ): Record<number, IWizardStepState> {
    return structuredClone(stepStates) as Record<number, IWizardStepState>;
  }
  
}
